// WelcomeVideoPlayer.jsx
import React, { useRef, useState, useEffect } from 'react';
import Thanks from '../../asset/videos/thank-you-video-virtual-scribe.webm';

// ---- Eager preload: starts buffering the moment this module is imported ----
// By the time the welcome modal opens, the video is already in the HTTP cache.
let __welcomeVideoPreloadPromise = null;

const preloadWelcomeVideo = () => {
  if (typeof window === "undefined") return Promise.resolve(null);
  if (__welcomeVideoPreloadPromise) return __welcomeVideoPreloadPromise;

  __welcomeVideoPreloadPromise = new Promise((resolve) => {
    const probe = document.createElement("video");
    probe.preload = "auto";
    probe.muted = true;
    probe.playsInline = true;
    probe.src = Thanks;

    const done = (ok) => resolve(ok ? probe : null);
    probe.addEventListener("loadeddata", () => done(true), { once: true });
    probe.addEventListener("error", () => done(false), { once: true });
    probe.load();

    // Keep a reference so the probe isn't GC'd before it finishes buffering.
    window.__welcomeVideoProbe = probe;
  });

  return __welcomeVideoPreloadPromise;
};

// Kick off immediately at import time.
preloadWelcomeVideo();
// ---------------------------------------------------------------------------

const WelcomeVideoPlayer = () => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [showCC, setShowCC] = useState(true);
  const [captionText, setCaptionText] = useState('');
  const [aspectRatio, setAspectRatio] = useState(16 / 9); // fallback until metadata loads

  // Autoplay — wait for enough data so play() doesn't stall on first frame
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let cancelled = false;

    const tryPlay = () => {
      if (cancelled) return;
      const p = video.play();
      if (p !== undefined) {
        p.then(() => !cancelled && setIsPlaying(true))
          .catch((err) => {
            console.warn("Autoplay was prevented:", err);
            if (!cancelled) setIsPlaying(false);
          });
      }
    };

    if (video.readyState >= 3) {
      tryPlay();
    } else {
      video.addEventListener("canplay", tryPlay, { once: true });
    }

    return () => {
      cancelled = true;
      video.removeEventListener("canplay", tryPlay);
    };
  }, []);

  // Handle custom caption rendering via cuechange
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleCueChange = () => {
      const track = video.textTracks?.[0];
      if (track && track.mode !== 'disabled' && track.activeCues && track.activeCues.length > 0) {
        // Combine multiple overlapping cues if necessary
        const activeText = Array.from(track.activeCues).map((cue) => cue.text).join('\n');
        setCaptionText(activeText);
      } else {
        setCaptionText('');
      }
    };

    const track = video.textTracks?.[0];
    if (track) {
      // 'hidden' means the track is active (fires events) but browser doesn't render it natively
      track.mode = 'hidden';
      track.addEventListener('cuechange', handleCueChange);
    }

    return () => {
      if (track) {
        track.removeEventListener('cuechange', handleCueChange);
      }
    };
  }, []);

  // Keep the CC button state in sync with the actual caption track mode.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const syncCCState = () => {
      const track = video.textTracks?.[0];
      if (track) {
        setShowCC(track.mode !== "disabled");
      }
    };

    syncCCState();
    video.addEventListener("loadedmetadata", syncCCState);
    if (video.textTracks?.addEventListener) {
      video.textTracks.addEventListener("addtrack", syncCCState);
    }

    return () => {
      video.removeEventListener("loadedmetadata", syncCCState);
      if (video.textTracks?.removeEventListener) {
        video.textTracks.removeEventListener("addtrack", syncCCState);
      }
    };
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
      setProgress((videoRef.current.currentTime / videoRef.current.duration) * 100 || 0);
    }
  };

  const handleLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video) return;
    setDuration(video.duration);

    if (video.videoWidth && video.videoHeight) {
      setAspectRatio(video.videoWidth / video.videoHeight);
    }

    // Ensure CC mode is set correctly on load
    const track = video.textTracks?.[0];
    if (track && showCC) {
      track.mode = "hidden";
    }
  };

  const handleSeek = (e) => {
    const seekTo = (e.target.value / 100) * duration;
    if (videoRef.current) {
      videoRef.current.currentTime = seekTo;
      setProgress(e.target.value);
    }
  };

  const toggleCC = () => {
    const track = videoRef.current?.textTracks?.[0];
    if (!track) return;

    const nextShowing = track.mode === "disabled"; // if currently disabled, turn on
    // 'hidden' means active but no native UI. 'disabled' means off completely.
    track.mode = nextShowing ? "hidden" : "disabled";
    setShowCC(nextShowing);
    if (!nextShowing) setCaptionText(''); // Clear text when turning off
  };

  const toggleFullscreen = () => {
    if (containerRef.current) {
      if (!document.fullscreenElement) {
        containerRef.current.requestFullscreen().catch(err => {
          console.error(`Error attempting full-screen mode: ${err.message}`);
        });
      } else {
        document.exitFullscreen();
      }
    }
  };

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  return (
    <div className="w-full flex justify-center">
      <div
        ref={containerRef}
        style={{ aspectRatio }}
        className="relative h-[50vh] sm:h-[60vh] lg:h-[70vh] max-w-full rounded-lg overflow-hidden group bg-black inline-block fullscreen:max-h-none fullscreen:h-screen fullscreen:rounded-none"
      >
        <video
          ref={videoRef}
          src={Thanks}
          autoPlay
          muted={isMuted}
          loop
          preload="auto"
          className="w-full h-full object-cover"
          playsInline
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
        >
          <track
            kind="captions"
            src="/videos/thank-you-video-virtual-scribe.vtt"
            srcLang="en"
            label="English"
            default
          />
          Your browser does not support the video tag.
        </video>

        {/* --- CUSTOM CAPTIONS ---
            Base position = controls hidden (closer to bottom).
            On group-hover (controls visible) it slides up out of the way. */}
        {showCC && captionText && (
          <div
            className="
              absolute left-0 right-0 flex justify-center px-2 sm:px-4
              pointer-events-none z-10
              transition-all duration-300 ease-out
              bottom-6 sm:bottom-8
              group-hover:bottom-16 sm:group-hover:bottom-20 md:group-hover:bottom-24
            "
          >
            <span className="bg-black/75 text-white text-xs sm:text-sm md:text-base leading-snug px-2.5 py-1.5 sm:px-4 sm:py-2 rounded-md sm:rounded-lg text-center max-w-[92%] sm:max-w-xl md:max-w-2xl drop-shadow-md">
              {captionText}
            </span>
          </div>
        )}

        {/* Custom Controls Overlay - Appears on hover */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-3 flex flex-col gap-2 transition-opacity duration-300 opacity-0 group-hover:opacity-100 z-20">

          {/* Progress Bar */}
          <input
            type="range"
            min="0"
            max="100"
            value={progress || 0}
            onChange={handleSeek}
            className="w-full h-1.5 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-500"
            aria-label="Video progress"
          />

          {/* Control Buttons */}
          <div className="flex items-center justify-between text-white">
            <div className="flex items-center gap-3">
              {/* Play/Pause */}
              <button onClick={togglePlay} className="hover:text-blue-400 transition-colors p-1" aria-label={isPlaying ? "Pause" : "Play"}>
                {isPlaying ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16"/><rect x="14" y="4" width="4" height="16"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                )}
              </button>

              {/* Volume */}
              <button onClick={toggleMute} className="hover:text-blue-400 transition-colors p-1" aria-label={isMuted ? "Unmute" : "Mute"}>
                {isMuted ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/></svg>
                )}
              </button>

              {/* Time */}
              <span className="text-xs font-medium tabular-nums">
                {formatTime(currentTime)} / {formatTime(duration)}
              </span>
            </div>

            <div className="flex items-center gap-3">
              {/* CC Button */}
              <button
                onClick={toggleCC}
                className={`px-1.5 py-0.5 rounded text-xs font-bold border transition-colors ${showCC ? 'bg-blue-600 border-blue-600 text-white' : 'bg-transparent border-white text-white hover:bg-white/20'}`}
                title="Toggle Captions"
                aria-label="Toggle Captions"
              >
                CC
              </button>

              {/* Fullscreen */}
              <button onClick={toggleFullscreen} className="hover:text-blue-400 transition-colors p-1" aria-label="Fullscreen">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeVideoPlayer;